import { Link } from "react-router-dom";
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { MainNav } from "@/components/layout/MainNav";
import { Seo } from "@/components/seo/Seo";
import { MoodDisplay } from "@/components/mood/MoodDisplay";
import { HomeSearch } from "@/components/home/HomeSearch";
import { HomeRotatingText, TextAnimationStyle } from "@/components/home/HomeRotatingText";
import { LiveLearnerCount } from "@/components/home/LiveLearnerCount";
import { useHomeData } from "@/hooks/useHomeData";
import { trackGtagConversion } from "@/lib/gtagConversion";
import { prefetchBrowseData } from "@/hooks/usePrefetchRoute";
import { ArrowRight, Sparkles, BookOpen, Brain, Zap } from "lucide-react";

// Lazy load ALL non-critical sections to reduce initial JS bundle
// These are below the fold and can load after first paint
const HomeContinueLearning = React.lazy(() =>
  import("@/components/home/HomeContinueLearning").then((mod) => ({
    default: mod.HomeContinueLearning,
  }))
);

const LearningPathsCarousel = React.lazy(() =>
  import("@/components/browse/LearningPathsCarousel").then((mod) => ({
    default: mod.LearningPathsCarousel,
  }))
);

const HomeRecentlyAdded = React.lazy(() =>
  import("@/components/home/HomeRecentlyAdded").then((mod) => ({
    default: mod.HomeRecentlyAdded,
  }))
);

export default function Home() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Single consolidated data fetch - eliminates race conditions
  const { data: homeData, isLoading } = useHomeData();

  // Prefetch /browse data when browser is idle for instant navigation
  React.useEffect(() => {
    const prefetch = () => prefetchBrowseData(queryClient);
    
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(prefetch, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    } else {
      // Fallback for Safari
      const timer = setTimeout(prefetch, 1500);
      return () => clearTimeout(timer);
    }
  }, [queryClient]);

  // Fire Google Ads conversion on homepage view (once per session)
  // Retry after delay since gtag loads lazily for LCP optimization
  React.useEffect(() => {
    const key = "gtag_home_conversion_sent";
    if (sessionStorage.getItem(key)) return;
    
    const tryConversion = () => {
      if (typeof window !== "undefined" && window.gtag) {
        sessionStorage.setItem(key, "1");
        trackGtagConversion();
        return true;
      }
      return false;
    };
    
    // Try immediately, then retry after gtag loads
    if (!tryConversion()) {
      const timer = setTimeout(tryConversion, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Content counts for social proof
  const courseCount = homeData?.courses?.length ?? 0;
  const quizCount = homeData?.quizLearnSets?.length ?? 0;

  // Cycle through different starter quizzes for "Start Here" CTA
  const starterTitles = ["AI Basics", "Giving Great Feedback", "Meeting Etiquette", "Focus & Deep Work", "Everyday Cybersecurity"];
  const [starterIdx, setStarterIdx] = React.useState(0);
  React.useEffect(() => {
    const interval = setInterval(() => setStarterIdx(i => (i + 1) % starterTitles.length), 8000);
    return () => clearInterval(interval);
  }, []);
  const currentStarterTitle = starterTitles[starterIdx];
  const starterQuiz = homeData?.quizLearnSets?.find(
    (set) => set.title.toLowerCase().includes(currentStarterTitle.toLowerCase())
  );
  const startHereLink = starterQuiz 
    ? `/quiz-learn?set=${starterQuiz.id}` 
    : "/quiz-learn";

  return (
    <div className="min-h-screen bg-gradient-to-b from-accent/30 via-background to-background overflow-x-hidden">
      <Seo
        title="mivobox.com | Free Micro-Learning for Tech Careers"
        description="Learn in micro moments with free, AI-curated flashcards and structured courses for tech career upskilling."
        canonicalPath="/"
        imageUrl="https://mivobox.com/og-home.png"
      />
      <MainNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="container px-6 pt-4 pb-6 md:pt-6 md:pb-8 relative">
          <div className="max-w-xl mx-auto relative">
            {/* Mood display - positioned to right of content on mobile, inline on larger screens */}
            <div className="absolute -top-1 right-0 sm:relative sm:float-right sm:ml-4 sm:-mt-1">
              <MoodDisplay />
            </div>
            
            {/* Live learner count - social proof */}
            <div className="mb-4 clear-both">
              <LiveLearnerCount />
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
              <span>Learn </span>
              <span className="text-primary">
                <HomeRotatingText words={homeData?.rotatingWords} animationStyle={homeData?.textAnimationStyle as TextAnimationStyle} />
              </span>
              <br />
              <span>in minutes</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Browse & Search Section */}
      <div className="container px-6 py-4 md:py-5">
        <div className="max-w-xl mx-auto">
          <HomeSearch 
            showQuiz={false}
            showSearch
            courses={homeData?.courses}
            quizLearnSets={homeData?.quizLearnSets}
            popularTags={homeData?.popularTags}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Quick Fire Quiz Section - extra bottom padding for phone nav bars */}
      <div className="container px-6 pt-5 pb-7 md:pt-7 md:pb-9">
        <div className="max-w-xl mx-auto">
          {/* Section label pill */}
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
              <Zap className="h-3 w-3" />
              What would you do?
            </span>
          </div>
          <HomeSearch
            showQuiz
            showSearch={false}
            endlessQuizQuestions={homeData?.endlessQuizQuestions}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Continue learning for signed-in users */}
      {user && (
        <div className="bg-muted/30 py-10 md:py-12">
          <React.Suspense fallback={null}>
            <HomeContinueLearning />
          </React.Suspense>
        </div>
      )}

      {/* Learning Paths - guided journeys (lazy loaded) */}
      <div className="py-8 md:py-10">
        <React.Suspense fallback={<div className="container px-6"><div className="max-w-xl mx-auto space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted/50 rounded-lg animate-pulse" />)}</div></div>}>
          <LearningPathsCarousel 
            learningPaths={homeData?.learningPaths}
            learningPathItems={homeData?.learningPathItems}
            isLoading={isLoading}
          />
        </React.Suspense>
      </div>

      {/* Recently Added (lazy loaded) */}
      <div className="bg-muted/20 py-8 md:py-10">
        <React.Suspense fallback={<div className="container px-6"><div className="max-w-xl mx-auto space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted/50 rounded-lg animate-pulse" />)}</div></div>}>
          <HomeRecentlyAdded 
            courses={homeData?.courses}
            quizLearnSets={homeData?.quizLearnSets}
            isLoading={isLoading}
          />
        </React.Suspense>
      </div>

      {/* Enhanced Footer */}
      <footer className="border-t mt-8 py-10 md:mt-12 md:py-12">
        <div className="container px-6">
          <div className="max-w-4xl mx-auto">
            {/* Top section: About + Links */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              {/* About */}
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <span className="font-semibold text-foreground">mivobox</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-md mb-3">
                  Free micro-learning platform for professionals. Master AI, workplace communication, career skills, and data literacy through bite-sized flashcards and interactive quizzes.
                </p>
                {/* Stats */}
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {courseCount > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      <BookOpen className="h-3 w-3 text-primary" />
                      {courseCount} courses
                    </span>
                  )}
                  {quizCount > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      <Brain className="h-3 w-3 text-primary" />
                      {quizCount} quizzes
                    </span>
                  )}
                  <Link 
                    to="/analytics" 
                    className="inline-flex items-center gap-1 text-primary hover:underline underline-offset-2"
                  >
                    View stats
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-medium text-sm text-foreground mb-3">Quick Links</h4>
                <div className="flex flex-col gap-2">
                  <Link to="/browse" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Browse Courses
                  </Link>
                  <Link to="/quiz-learn" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Take a Quiz
                  </Link>
                  <Link to="/products" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Our Products
                  </Link>
                  <Link to="/about" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    About Us
                  </Link>
                  <Link to="/contact" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Contact
                  </Link>
                </div>
              </div>
            </div>

            {/* Bottom section: Copyright + Legal */}
            <div className="pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">
                © {new Date().getFullYear()} mivobox.com. All rights reserved.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs">
                {!user && (
                  <>
                    <Link to="/login" className="text-primary font-medium hover:underline underline-offset-4">
                      Sign in
                    </Link>
                    <span className="text-border">·</span>
                  </>
                )}
                <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                  Terms
                </Link>
                <span className="text-border">·</span>
                <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
                  Privacy
                </Link>
                <span className="text-border">·</span>
                <Link to="/disclaimer" className="text-muted-foreground hover:text-foreground transition-colors">
                  Disclaimer
                </Link>
                <span className="text-border">·</span>
                <Link to="/refunds" className="text-muted-foreground hover:text-foreground transition-colors">
                  Refunds
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}